/**
 * Knowledge Graph Embeddings (lib/knowledge-graph/embeddings.ts)
 *
 * Turns a graph node's human content (title + summary) into a stable embedding
 * text, hashes it for change-detection, and produces a 1536-dim vector via the
 * platform's Bedrock (Cohere Embed v4) client. This is what lets the graph be
 * traversed by MEANING, not only structural edges.
 *
 * Primary responsibilities:
 * - buildEmbeddingText: deterministic text from a node's title/summary/type
 * - contentHash: stable hash of that text (skip re-embedding unchanged nodes)
 * - embedText / embedNode: produce a vector via Bedrock, returning null when
 *   embeddings are unavailable (no auth) or the node has no content
 *
 * Dependencies:
 * - lib/portkey-embeddings (createOpenAIEmbeddingsClient, canUseOpenAIEmbeddings)
 * - lib/bedrock/embeddings (dims/model constants)
 * - node:crypto (sha256)
 */

import { createHash } from 'crypto'

import {
  BEDROCK_EMBEDDING_DIMENSIONS,
  BEDROCK_EMBEDDING_MODEL
} from '@/lib/bedrock/embeddings'
import {
  canUseOpenAIEmbeddings,
  createOpenAIEmbeddingsClient
} from '@/lib/portkey-embeddings'

// --------------

export interface EmbeddableNode {
  nodeType: string
  title: string | null
  summary: string | null
}

/**
 * Build the deterministic text that represents a node for embedding. Combines
 * the node type (as a coarse semantic hint), title, and summary. Returns null
 * when there is no meaningful content to embed (so we never embed empty text).
 */
export function buildEmbeddingText(node: EmbeddableNode): string | null {
  const parts: string[] = []

  const typeLabel = node.nodeType.replace(/_/g, ' ').trim()
  if (typeLabel) parts.push(typeLabel)

  const title = node.title?.trim()
  if (title) parts.push(title)

  const summary = node.summary?.trim()
  if (summary) parts.push(summary)

  // Require at least a title or summary — the bare type alone is not content.
  if (!title && !summary) return null

  return parts.join('\n').slice(0, 8000)
}

/** Stable content hash of the embedding text, for change-detection. */
export function contentHash(text: string): string {
  return createHash('sha256').update(text).digest('hex')
}

// --------------

export interface EmbeddingResult {
  embedding: number[]
  contentHash: string
  text: string
}

/**
 * Whether the node needs (re)embedding given its previously stored hash. A
 * node with no embeddable content never needs embedding.
 */
export function needsEmbedding(
  node: EmbeddableNode,
  previousHash: string | null | undefined
): boolean {
  const text = buildEmbeddingText(node)
  if (!text) return false
  return contentHash(text) !== (previousHash ?? '')
}

/** True when embeddings can actually be produced in this environment. */
export function embeddingsAvailable(): boolean {
  return canUseOpenAIEmbeddings()
}

/**
 * Embed arbitrary text. Returns null when embeddings are unavailable (no auth)
 * so callers can degrade gracefully instead of throwing into a write path.
 */
export async function embedText(text: string): Promise<number[] | null> {
  if (!text.trim()) return null
  if (!embeddingsAvailable()) return null

  const client = createOpenAIEmbeddingsClient()
  const res = await client.embeddings.create({
    input: text,
    model: BEDROCK_EMBEDDING_MODEL,
    dimensions: BEDROCK_EMBEDDING_DIMENSIONS
  })

  const embedding = res.data?.[0]?.embedding
  return Array.isArray(embedding) ? embedding : null
}

/**
 * Embed a node's content. Returns null when there is nothing to embed or when
 * embeddings are unavailable. Includes the content hash + text so callers can
 * persist them for change-detection.
 */
export async function embedNode(node: EmbeddableNode): Promise<EmbeddingResult | null> {
  const text = buildEmbeddingText(node)
  if (!text) return null

  const embedding = await embedText(text)
  if (!embedding) return null

  return { embedding, contentHash: contentHash(text), text }
}

// --------------

/**
 * Backfill embeddings for graph nodes that are missing them (or whose content
 * changed). Intended to run from a cron/job where AWS_BEARER_TOKEN_BEDROCK is
 * present. Bounded by `limit`; returns a summary. Never throws for a single
 * node — per-node failures are counted so one bad row cannot abort the batch.
 *
 * This is deliberately in the lib (not db) layer so it can compose the pure
 * embedding helpers with the typed db reads/writes without a circular import.
 */
export interface EmbeddingBackfillSummary {
  scanned: number
  embedded: number
  skipped: number
  errors: number
}

export async function backfillNodeEmbeddings(
  input: {
    workspaceId: string
    limit?: number
    listUnembedded: (
      workspaceId: string,
      limit: number
    ) => Promise<
      Array<{ id: string; nodeType: string; title: string | null; summary: string | null; contentHash: string | null }>
    >
    persist: (nodeId: string, embedding: number[], hash: string) => Promise<void>
  }
): Promise<EmbeddingBackfillSummary> {
  const summary: EmbeddingBackfillSummary = { scanned: 0, embedded: 0, skipped: 0, errors: 0 }

  if (!embeddingsAvailable()) return summary

  const limit = Math.max(1, Math.min(input.limit ?? 500, 5000))
  const rows = await input.listUnembedded(input.workspaceId, limit)

  for (const row of rows) {
    summary.scanned += 1
    try {
      if (!needsEmbedding(row, row.contentHash)) {
        summary.skipped += 1
        continue
      }
      const result = await embedNode(row)
      if (!result) {
        summary.skipped += 1
        continue
      }
      await input.persist(row.id, result.embedding, result.contentHash)
      summary.embedded += 1
    } catch {
      summary.errors += 1
    }
  }

  return summary
}
